import { Sheet, SheetContent, SheetTitle, SheetDescription } from './ui/sheet';
import { X, PlugZap, Hotel, Coffee, ShoppingBag, Store, Dumbbell, TreePine, Landmark, Bike, Bus, Car, Train, Plane, ParkingCircle, Fuel, Wifi, Zap, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Vehicle, Filters } from '../../types';
import { POWER_CATEGORIES, type PowerCategoryId } from '../../utils/powerCategories';

interface FilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  vehicles?: Vehicle[];
}

// Connector chips store backend IDs (evse.connectorType); labels are display-only.
const CONNECTOR_TYPES = [
  { id: 'type2', label: 'Type 2 AC' },
  { id: 'ccs2', label: 'CCS DC' },
  { id: 'chademo', label: 'CHAdeMO' },
];

// Available facilities with icons — values match backend Facility enum
const FACILITIES = [
  { value: 'HOTEL', label: 'Hotel', icon: Hotel },
  { value: 'RESTAURANT', label: 'Restaurant', icon: Coffee },
  { value: 'CAFE', label: 'Café', icon: Coffee },
  { value: 'MALL', label: 'Mall', icon: ShoppingBag },
  { value: 'SUPERMARKET', label: 'Market', icon: Store },
  { value: 'SPORT', label: 'Sport', icon: Dumbbell },
  { value: 'RECREATION_AREA', label: 'Recreation', icon: Dumbbell },
  { value: 'NATURE', label: 'Nature', icon: TreePine },
  { value: 'MUSEUM', label: 'Museum', icon: Landmark },
  { value: 'BIKE_SHARING', label: 'Bike', icon: Bike },
  { value: 'BUS_STOP', label: 'Bus', icon: Bus },
  { value: 'TAXI_STAND', label: 'Taxi', icon: Car },
  { value: 'METRO_STATION', label: 'Metro', icon: Train },
  { value: 'TRAIN_STATION', label: 'Train', icon: Train },
  { value: 'AIRPORT', label: 'Airport', icon: Plane },
  { value: 'PARKING_LOT', label: 'Parking', icon: ParkingCircle },
  { value: 'CARPOOL_PARKING', label: 'Carpool', icon: Car },
  { value: 'FUEL_STATION', label: 'Fuel', icon: Fuel },
  { value: 'WIFI', label: 'WiFi', icon: Wifi },
];

export function FilterPanel({ open, onOpenChange, filters, onFiltersChange, vehicles }: FilterPanelProps) {
  const toggleConnectorType = (type: string) => {
    const newTypes = filters.connectorTypes.includes(type)
      ? filters.connectorTypes.filter(t => t !== type)
      : [...filters.connectorTypes, type];
    
    onFiltersChange({ ...filters, connectorTypes: newTypes });
  };

  const toggleFacility = (facility: string) => {
    const newFacilities = filters.facilities.includes(facility)
      ? filters.facilities.filter(f => f !== facility)
      : [...filters.facilities, facility];

    onFiltersChange({ ...filters, facilities: newFacilities });
  };

  const togglePowerCategory = (id: PowerCategoryId) => {
    const newCategories = filters.powerCategories.includes(id)
      ? filters.powerCategories.filter(c => c !== id)
      : [...filters.powerCategories, id];

    onFiltersChange({ ...filters, powerCategories: newCategories });
  };

  const selectVehicle = (vehicleId: string) => {
    // Toggle selection: if already selected, deselect it; otherwise select it
    const newVehicleId = filters.selectedVehicleId === vehicleId ? undefined : vehicleId;
    onFiltersChange({ ...filters, selectedVehicleId: newVehicleId });
  };

  const clearAllFilters = () => {
    onFiltersChange({ connectorTypes: [], facilities: [], powerCategories: [], selectedVehicleId: undefined });
  };

  const hasActiveFilters = filters.connectorTypes.length > 0 || filters.facilities.length > 0 || filters.powerCategories.length > 0 || !!filters.selectedVehicleId;

  const getVehicleIcon = (type: string) => {
    switch (type) {
      case '2wheeler':
        return Bike;
      case '3wheeler':
        return Car;
      case '4wheeler':
        return Car;
      default:
        return Car;
    }
  };

  const hasVehicles = vehicles && vehicles.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-[32px] border-0 p-0 bg-white dark:bg-gray-900" showCloseButton={false}>
        <SheetTitle className="sr-only">Filter Options</SheetTitle>
        <SheetDescription className="sr-only">
          Filter charging stations by connector type and facilities
        </SheetDescription>
        
        {/* Handle Bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl text-gray-900 dark:text-white">Filters</h2>
            {hasActiveFilters && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {(filters.connectorTypes.length + filters.facilities.length + filters.powerCategories.length + (filters.selectedVehicleId ? 1 : 0))} active
              </p>
            )}
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-5 py-6" style={{ maxHeight: 'calc(85vh - 180px)' }}>
          
          {/* My Vehicles Section - Most Prominent */}
          {hasVehicles ? (
            <div className="mb-8">
              <div className="bg-gradient-to-br from-[#8bc43e]/10 to-[#6fa02e]/10 dark:from-[#8bc43e]/20 dark:to-[#6fa02e]/20 rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#8bc43e] flex items-center justify-center">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">My Vehicles</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Find compatible chargers</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {vehicles.map((vehicle) => {
                    const isSelected = filters.selectedVehicleId === vehicle.id;
                    const VehicleIcon = getVehicleIcon(vehicle.vehicleType ?? '');
                    
                    return (
                      <button
                        key={vehicle.id}
                        onClick={() => selectVehicle(vehicle.id)}
                        className={`w-full p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-[#8bc43e] bg-[#8bc43e] shadow-lg scale-[1.02]'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-[#8bc43e]/50 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isSelected ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            <VehicleIcon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`} />
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`font-bold text-sm ${isSelected ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                              {vehicle.make} {vehicle.model}
                            </p>
                            {vehicle.registrationNumber && (
                              <p className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                                {vehicle.registrationNumber}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-[#8bc43e]" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                {filters.selectedVehicleId && (
                  <div className="mt-3 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2">
                    <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Showing chargers compatible with your vehicle
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Connector Types Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <PlugZap className="w-5 h-5 text-[#8bc43e]" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Connector Type</h3>
            </div>
            <div className="space-y-2">
              {CONNECTOR_TYPES.map((type) => {
                const isSelected = filters.connectorTypes.includes(type.id);
                return (
                  <button
                    key={type.id}
                    onClick={() => toggleConnectorType(type.id)}
                    className={`w-full px-4 py-3 rounded-xl border-2 transition-all text-left font-medium ${
                      isSelected
                        ? 'border-[#8bc43e] bg-[#8bc43e]/5 dark:bg-[#8bc43e]/10 text-gray-900 dark:text-white'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{type.label}</span>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-[#8bc43e] flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Charging Speed Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[#8bc43e]" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Charging Speed</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {POWER_CATEGORIES.map((cat) => {
                const isSelected = filters.powerCategories.includes(cat.id);

                return (
                  <button
                    key={cat.id}
                    onClick={() => togglePowerCategory(cat.id)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? `${cat.borderClass} bg-opacity-5`
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    style={isSelected ? { backgroundColor: `${cat.color}10` } : undefined}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? '' : 'bg-gray-100 dark:bg-gray-700'
                      }`}
                      style={isSelected ? { backgroundColor: cat.color } : undefined}
                    >
                      <span className={`inline-flex items-center -space-x-1 ${isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                        {Array.from({ length: cat.boltCount }).map((_, i) => (
                          <Zap key={i} className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                        ))}
                      </span>
                    </div>
                    <div className="text-center">
                      <span className={`text-xs font-medium block ${
                        isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {cat.name}
                      </span>
                      <span className={`text-[10px] block ${
                        isSelected ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {cat.subtitle}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Facilities Section */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Facilities</h3>
            <div className="grid grid-cols-3 gap-3">
              {FACILITIES.map((facility) => {
                const isSelected = filters.facilities.includes(facility.value);
                const Icon = facility.icon;
                
                return (
                  <button
                    key={facility.value}
                    onClick={() => toggleFacility(facility.value)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-[#8bc43e] bg-[#8bc43e]/5 dark:bg-[#8bc43e]/10'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-[#8bc43e]' : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`} />
                    </div>
                    <span className={`text-xs font-medium text-center ${
                      isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {facility.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-5 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
              className="flex-1 h-12 rounded-xl border-2 font-semibold disabled:opacity-50"
            >
              Clear All
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              className="flex-1 h-12 rounded-xl bg-[#8bc43e] hover:bg-[#6fa02e] text-white font-semibold"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}